// =============================================================================
// TemplatePickerModal.jsx
// =============================================================================
// Modal used inside the request detail view when a staff/responder is
// composing the approval-summary comment and wants to insert a canned snippet
// from the comment_templates table.
//
// Separate from the same-named modal in DashboardPages.jsx — that one's a
// generic comment picker used elsewhere. This one filters out _NextSteps
// templates from the visible list, and if a chosen template has an
// `appends_next_steps` pointer it concatenates the linked Next-Steps text.
//
// Props:
//   onSelect(text) - called with the resolved snippet text
//   onClose()      - dismiss the modal
// =============================================================================
import React from "react";
import { useCommentTemplates } from "../../hooks/useCommentTemplates";
import { ModalBackdrop } from "../form/FormUI.jsx";

export function TemplatePickerModal({ onSelect, onClose }) {
  var _db = useCommentTemplates();
  var allTemplates = _db.templates;
  var loading = _db.loading;
  const [search, setSearch] = React.useState("");
  const [cat, setCat] = React.useState("All");
  var visibleTemplates = allTemplates.filter(function (t) {
    return t.category !== "_NextSteps";
  });
  var categories = ["All"].concat(
    Array.from(
      new Set(
        visibleTemplates.map(function (t) {
          return t.category;
        })
      )
    )
  );
  const filtered = visibleTemplates.filter(function (t) {
    var matchCat = cat === "All" || t.category === cat;
    var q = search.toLowerCase();
    var matchSearch =
      !q ||
      t.title.toLowerCase().includes(q) ||
      t.text.toLowerCase().includes(q) ||
      t.category.toLowerCase().includes(q);
    return matchCat && matchSearch;
  });
  function handleSelect(t) {
    var text = t.text;
    if (t.appends_next_steps || t.appendsNextSteps) {
      var nsId = t.appends_next_steps || t.appendsNextSteps;
      var nsTemplate = allTemplates.find(function (x) {
        return x.id === nsId;
      });
      if (nsTemplate?.text) {
        text = text ? `${text}\n\n${nsTemplate.text}` : nsTemplate.text;
      }
    }
    onSelect(text);
    onClose();
  }
  return (
    <ModalBackdrop
      onClose={onClose}
      contentStyle={{
        background: "white",
        borderRadius: 14,
        width: "calc(100vw - 40px)",
        maxWidth: 620,
        maxHeight: "80vh",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
      }}
    >
      <div className="py-4 px-5 border-b border-zephyr-border flex justify-between items-center">
        <div className="font-bold text-base text-zephyr-teal-dark">Insert Template</div>
        <button
          onClick={onClose}
          className="text-[20px] cursor-pointer text-zephyr-muted leading-none"
          style={{ background: "none", border: "none" }}
        >
          ×
        </button>
      </div>
      <div className="py-3 px-5 border-b border-zephyr-border flex gap-[10px]">
        <input
          /* biome-ignore lint/a11y/noAutofocus: deliberate UX — focus search box on modal open */
          autoFocus
          value={search}
          onChange={function (e) {
            setSearch(e.target.value);
          }}
          placeholder="Search templates..."
          className="flex-1 py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px]"
          style={{ fontFamily: "inherit" }}
        />
        <select
          value={cat}
          onChange={function (e) {
            setCat(e.target.value);
          }}
          className="py-2 px-[10px] rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
          style={{ fontFamily: "inherit" }}
        >
          {categories.map(function (c) {
            return <option key={c}>{c}</option>;
          })}
        </select>
      </div>
      <div className="overflow-y-auto flex-1 py-2">
        {loading && (
          <div className="p-5 text-center text-zephyr-muted text-[13px]">Loading templates...</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="p-5 text-center text-zephyr-muted text-[13px]">
            No templates match your search.
          </div>
        )}
        {!loading &&
          filtered.map(function (t) {
            var hasNextSteps = !!(t.appends_next_steps || t.appendsNextSteps);
            return (
              <button
                type="button"
                key={t.id}
                onClick={function () {
                  handleSelect(t);
                }}
                className="block w-full text-left bg-transparent border-none border-b border-zephyr-off-white py-3 px-5 cursor-pointer hover:bg-zephyr-teal-light"
                style={{ fontFamily: "inherit" }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[11px] font-bold text-white bg-zephyr-teal rounded-[4px] py-0.5 px-2">
                    {t.category}
                  </span>
                  <span className="font-semibold text-[13px] text-zephyr-text">{t.title}</span>
                  {hasNextSteps && (
                    <span className="text-[10px] font-bold text-zephyr-blue bg-zephyr-blue-light border border-[#90CAF9] rounded-[4px] py-px px-[6px] whitespace-nowrap">
                      + Next Steps
                    </span>
                  )}
                </div>
                {/* webkit line-clamp must stay inline */}
                <div
                  style={{
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                  className="text-xs text-zephyr-muted leading-[1.5]"
                >
                  {t.text}
                </div>
              </button>
            );
          })}
      </div>
      <div className="py-[10px] px-5 border-t border-zephyr-border text-xs text-zephyr-muted">
        Click a template to insert it into your comment. Templates marked{" "}
        <strong>+ Next Steps</strong> will automatically include the relevant next steps. You can
        edit before sending.
      </div>
    </ModalBackdrop>
  );
}
